import { useMatch } from '@tanstack/react-router';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GLOBAL_NAVIGATION_HEIGHT } from '@/shared/lib/constants';
import { type AIContext, useRouteSidePanelStore } from '@/shared/stores/side-panel-store';
import { StatusCard } from '@/shared/ui';
import { ProjectCreateAIChatHeader } from './project-create/header/ProjectCreateAIChatHeader';
import { ProjectCreateAIChatContents } from './project-create/ProjectCreateAIChatContents';
import { ProjectDetailAIChatHeader } from './project-detail/header/ProjectDetailAIChatHeader';
import { ProjectDetailAIChatContents } from './project-detail/ProjectDetailAIChatContents';

type Props = {
  aiContextMenu: AIContext | undefined;
  setAiContextMenu: (context: AIContext | undefined) => void;
};

export const AIChatContents: FC<Props> = ({ aiContextMenu, setAiContextMenu }) => {
  const requestClose = useRouteSidePanelStore((state) => state.requestClose);
  const { t } = useTranslation();
  const [showHistory, setShowHistory] = useState(false);

  // useMatch で型安全にルートマッチを確認
  // 編集ページは詳細ページ（$projectId/）にもマッチするため、先に判定する
  const isProjectEditPage = useMatch({
    from: '/_protected/projects/$projectId/edit',
    shouldThrow: false,
  });
  const isProjectDetailPage = useMatch({
    from: '/_protected/projects/$projectId/',
    shouldThrow: false,
  });
  const isProjectMemoPage = useMatch({
    from: '/_protected/projects/$projectId/memo',
    shouldThrow: false,
  });
  const isProjectCreatePage = useMatch({
    from: '/_protected/projects/create',
    shouldThrow: false,
  });

  // 事業詳細ページとメモページは同じ AI チャット UI を共有する
  const projectDetailLikeMatch = isProjectDetailPage ?? isProjectMemoPage;

  return (
    <div
      className="relative flex flex-col"
      style={{ height: `calc(100vh - ${GLOBAL_NAVIGATION_HEIGHT}px)` }}
    >
      {isProjectEditPage || isProjectCreatePage ? (
        <>
          <ProjectCreateAIChatHeader
            aiContextMenu={aiContextMenu}
            setAiContextMenu={setAiContextMenu}
          />
          <div className="p-4">
            <ProjectCreateAIChatContents
              aiContextMenu={aiContextMenu}
              setAiContextMenu={setAiContextMenu}
            />
          </div>
        </>
      ) : projectDetailLikeMatch ? (
        <>
          <ProjectDetailAIChatHeader
            projectId={projectDetailLikeMatch.params.projectId}
            showHistory={showHistory}
            setShowHistory={setShowHistory}
            threadId={
              aiContextMenu?.route === 'projectDetail' && aiContextMenu.mode === 'agent_chat'
                ? aiContextMenu.threadId
                : undefined
            }
          />
          <div className="min-h-0 flex-1 px-4 py-3">
            <ProjectDetailAIChatContents
              projectId={projectDetailLikeMatch.params.projectId}
              aiContextMenu={aiContextMenu}
              setAiContextMenu={setAiContextMenu}
              showHistory={showHistory}
              setShowHistory={setShowHistory}
            />
          </div>
        </>
      ) : (
        <StatusCard
          title={t('ai.menu.under-development-title')}
          description={t('ai.menu.under-development-description')}
          buttonText={t('common.actions.close')}
          onButtonClick={requestClose}
        />
      )}
    </div>
  );
};
